import type { PlatformPolicyConfig } from './types';
import type { UserProfile } from '../contexts/AuthContext';
import {
  computeAdhikara,
  createSangatiScoreEvent,
  detectSwabhavaJaAnomaly,
  isQualifiedAcharya,
  isQualifiedRaja,
  type ArchetypeKey,
  type SubScores,
} from '../domain/adhikara';
import { getStage } from '../domain/adhikara';
import { getArchetypeWeights as getPolicyArchetypeWeights } from '../domain/adhikara';

type SwabhavaKey = 'anveshak' | 'setu' | 'rakshak' | 'nirmata';

type AdhikaraScores = UserProfile['roleGateway']['adhikara'];

export interface GatewayProgress {
  ready: boolean;
  completed: number;
  total: number;
  percent: number;
  sangatiHours: number;
  sahacharyaValue: number;
  adhikaraScore: number | null;
  stageKey: string | null;
}

function hasText(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim().length > 0;
  return !!value;
}

function toArchetypeKey(value: string): ArchetypeKey | null {
  const upper = value.trim().toUpperCase();
  if (upper === 'BRAHMIN' || upper === 'KSHATRIYA' || upper === 'VAISHYA' || upper === 'SHUDRA') return upper;
  return null;
}

export function resolvePolicyArchetype(value: string): ArchetypeKey | null {
  const normalized = value.trim().toLowerCase() as SwabhavaKey | string;
  if (normalized === 'anveshak') return 'BRAHMIN';
  if (normalized === 'setu') return 'VAISHYA';
  if (normalized === 'rakshak') return 'KSHATRIYA';
  if (normalized === 'nirmata') return 'SHUDRA';
  return toArchetypeKey(value);
}

export function getArchetypeWeights(policy: PlatformPolicyConfig | null | undefined, archetype: string): Record<string, number> | null {
  const key = resolvePolicyArchetype(archetype);
  if (!key) return null;
  return getPolicyArchetypeWeights(policy, key);
}

export function deriveStageKey(score: number, policy?: PlatformPolicyConfig | null): string | null {
  const stage = getStage(score, policy);
  if (!stage) return null;
  return stage === 'BIJA' ? 'seed'
    : stage === 'ANKURA' ? 'sprout'
      : stage === 'PARNA' ? 'leaf'
        : stage === 'PUSHPA' ? 'bloom'
          : 'fruit';
}

export function computeAdhikaraScore(scores: AdhikaraScores, archetype: string, policy?: PlatformPolicyConfig | null): number | null {
  const key = resolvePolicyArchetype(archetype);
  if (!key) return null;
  return computeAdhikara({
    hridaya: scores.hridaya,
    vak: scores.vak,
    jnana: scores.jnana,
    karma: scores.karma,
    phala: scores.phala,
  }, key, policy).score;
}

function countConditions(conditions: boolean[]): { completed: number; total: number } {
  const total = conditions.length;
  const completed = conditions.filter(Boolean).length;
  return { completed, total };
}

function hasBasicProfile(profile: UserProfile): boolean {
  return [
    profile.alias,
    profile.age,
    profile.nativeLanguage,
    profile.preferredScript,
    profile.timeZone,
    profile.learningGoals,
    profile.sessionModes,
    profile.lookingFor,
    profile.availability,
  ].every(hasText);
}

export function calculateGatewayProgress(profile: UserProfile, policy?: PlatformPolicyConfig | null): GatewayProgress {
  const gateway = profile.roleGateway;
  const score = computeAdhikaraScore(gateway.adhikara, gateway.primaryArchetype, policy);
  const stageKey = score === null ? null : deriveStageKey(score, policy);
  const baseConditions = [
    hasBasicProfile(profile),
    hasText(gateway.selectedRole),
    hasText(gateway.city),
    hasText(gateway.adhikara.hridaya),
    hasText(gateway.adhikara.vak),
    hasText(gateway.adhikara.jnana),
    hasText(gateway.adhikara.karma),
    hasText(gateway.adhikara.phala),
  ];

  const conditions = [...baseConditions];

  if (gateway.selectedRole === 'shiksharthi') {
    conditions.push(hasText(gateway.primaryArchetype), hasText(gateway.secondaryArchetype));
  }

  if (gateway.selectedRole === 'upadhyaya') {
    const minimum = Number(policy?.roles?.upadhyaya && typeof policy.roles.upadhyaya === 'object'
      ? (policy.roles.upadhyaya as { qualificationCriteria?: { minAdhikaraScore?: number } }).qualificationCriteria?.minAdhikaraScore
      : undefined);
    conditions.push(hasText(gateway.primaryArchetype), Number.isFinite(score) && score !== null && score >= minimum);
  }

  if (gateway.selectedRole === 'acharya') {
    conditions.push(
      isQualifiedAcharya({
        criteria: {
          vriddha: gateway.acharya.shravana || gateway.acharya.smarana || gateway.acharya.samvada,
          alolupa: gateway.acharya.niyama,
          atmavant: gateway.acharya.vinaya,
          dambhahin: gateway.acharya.dambhahin,
          samyagvinita: gateway.acharya.seva,
          mridu: gateway.acharya.smarana,
          witnessApproved: gateway.guruAttestationStatus === 'approved',
        },
        adhikaraScore: score,
      }),
    );
  }

  if (gateway.selectedRole === 'guru') {
    conditions.push(gateway.guruAttestationStatus === 'approved');
  }

  if (gateway.selectedRole === 'raja') {
    conditions.push(
      isQualifiedRaja({
        criteria: {
          dana: gateway.raja.dana,
          vrata: gateway.raja.vrata,
          brahmacharya: gateway.raja.brahmacharya,
          brahma_dharanam: gateway.raja.brahma_dharanam,
          dama: gateway.raja.dama,
          prashantata: gateway.raja.prashantata,
          bhuta_anukampana: gateway.raja.bhuta_anukampana,
          anrishansya: gateway.raja.anrishansya,
          parasva_adana_varjanam: gateway.raja.parasva_adana_varjanam,
          vyalika_karanam_tyaga: gateway.raja.vyalika_karanam_tyaga,
          matapitru_shushrusha: gateway.raja.matapitru_shushrusha,
          devata_atithi_guru_pujanam: gateway.raja.devata_atithi_guru_pujanam,
          ghrina: gateway.raja.ghrina,
          nitya_indriya_sanyama: gateway.raja.nitya_indriya_sanyama,
        },
      }),
    );
  }

  const { completed, total } = countConditions(conditions);
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  return {
    ready: total > 0 && completed === total,
    completed,
    total,
    percent,
    sangatiHours: completed,
    sahacharyaValue: Math.max(0, Math.min(12, Math.round((completed / Math.max(total, 1)) * 12))),
    adhikaraScore: score,
    stageKey,
  };
}

export function detectArchetypeMismatch(
  registeredArchetype: ArchetypeKey,
  scores: SubScores,
  policy?: PlatformPolicyConfig | null,
) {
  return detectSwabhavaJaAnomaly(registeredArchetype, scores, policy);
}

export function emitGatewayMetrics(profile: UserProfile, policy?: PlatformPolicyConfig | null, reason = 'state-change'): GatewayProgress {
  const metrics = calculateGatewayProgress(profile, policy);
  const payload = createSangatiScoreEvent({
    reason,
    uid: profile.uid,
    archetype: resolvePolicyArchetype(profile.roleGateway.primaryArchetype) ?? 'BRAHMIN',
    score: metrics.adhikaraScore ?? 0,
    stage: metrics.stageKey
      ? metrics.stageKey === 'seed'
        ? 'BIJA'
        : metrics.stageKey === 'sprout'
          ? 'ANKURA'
          : metrics.stageKey === 'leaf'
            ? 'PARNA'
            : metrics.stageKey === 'bloom'
              ? 'PUSHPA'
              : 'PHALA'
      : null,
    trace: [],
  });
  window.dispatchEvent(new CustomEvent('garuda:gateway-metrics', { detail: { ...payload, ready: metrics.ready, sahacharyaValue: metrics.sahacharyaValue } }));
  console.info('[GatewayMetrics]', payload);
  return metrics;
}

export interface SevaProviderStatus {
  enabled: boolean;
  /** True only when enabled + dimensions selected + assertion ticked */
  readyToRequest: boolean;
  /** Cached from backend — display only, never authoritative */
  cachedReviewStatus: 'not_requested' | 'pending' | 'approved' | 'rejected';
}

/**
 * Separate from calculateGatewayProgress — seva provider activation is
 * opt-in and must never block gateway completion for regular users.
 */
export function computeSevaProviderStatus(profile: UserProfile): SevaProviderStatus {
  const sp = profile.roleGateway.sevaProvider;
  if (!sp.enabled) {
    return { enabled: false, readyToRequest: false, cachedReviewStatus: 'not_requested' };
  }
  return {
    enabled: true,
    readyToRequest: sp.dimensions.length > 0 && sp.serviceAssertions,
    cachedReviewStatus: sp.cachedReviewStatus,
  };
}
